import React from "react";

import SelectMenu from "../controls/SelectMenu";
import { excludeAllOption } from "../../shared/filterUtils";

const FIELD_SELECT_CLASS = "w-[min(13rem,100%)]";

interface SelectOption {
  value: number | string;
  label: string;
}

type MultiFilterKey =
  | "product"
  | "category"
  | "brand"
  | "supervisor"
  | "employee1";

interface PackagingInlineFiltersProps {
  filters: {
    product: Array<number | string>;
    category: Array<number | string>;
    brand: Array<number | string>;
    supervisor: Array<number | string>;
    employee1: Array<number | string>;
  };
  onFilterChange: (nextFilters: PackagingInlineFiltersProps["filters"]) => void;
  productOptions: SelectOption[];
  categoryOptions: SelectOption[];
  brandOptions: SelectOption[];
  supervisorOptions: SelectOption[];
  employeeOptions: SelectOption[];
}

function FilterField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-w-[9rem] flex-col gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#64748B]">
        {label}
      </span>
      {children}
    </div>
  );
}

export default function PackagingInlineFilters({
  filters,
  onFilterChange,
  productOptions,
  categoryOptions,
  brandOptions,
  supervisorOptions,
  employeeOptions,
}: PackagingInlineFiltersProps) {
  const patchFilter = <K extends keyof PackagingInlineFiltersProps["filters"]>(
    key: K,
    value: PackagingInlineFiltersProps["filters"][K],
  ) => {
    onFilterChange({
      ...filters,
      [key]: value,
    });
  };

  const renderMultiSelect = (
    key: MultiFilterKey,
    label: string,
    options: SelectOption[],
  ) => {
    const selectableOptions = excludeAllOption(options);

    return (
      <FilterField label={label}>
        <SelectMenu
          name={key}
          options={selectableOptions}
          value={filters[key]}
          parentClass={FIELD_SELECT_CLASS}
          onValueChange={(nextValue: Array<number | string> | null) =>
            patchFilter(key, nextValue ?? [])
          }
          isMulti
          filter={selectableOptions.length > 8}
          placeholder="Select..."
          menuPortalTarget={
            typeof document !== "undefined" ? document.body : null
          }
        />
      </FilterField>
    );
  };

  return (
    <div className="flex flex-wrap items-end gap-x-3 gap-y-3">
      {renderMultiSelect("product", "Product", productOptions)}
      {renderMultiSelect("category", "Category", categoryOptions)}
      {renderMultiSelect("brand", "Brand", brandOptions)}
      {renderMultiSelect("supervisor", "Supervisor", supervisorOptions)}
      {renderMultiSelect("employee1", "Employee", employeeOptions)}
    </div>
  );
}
